/**
 * Normalize theme list fields from fetchThemeData: API may return arrays or JSON-encoded strings.
 * (Admin themeupdate uses the same logic as ensureArray.)
 */
export function ensureThemeJsonArray(val) {
  if (Array.isArray(val)) return val;
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const p = JSON.parse(val);
      return Array.isArray(p) ? p : [];
    } catch {
      return [];
    }
  }
  return [];
}

/**
 * Build a safe URL for theme images (S3 keys under REACT_APP_S3_PATH, or full https URLs).
 * Encodes each path segment so spaces, parentheses, %, etc. resolve to the correct object key.
 */
export function getDvSourceImageUrl(imgPath) {
  if (!imgPath) return "";
  const s = String(imgPath).trim();
  if (!s) return "";
  const encodePathSegments = (relativePath) =>
    relativePath
      .split("/")
      .map((seg) => {
        if (!seg) return seg;
        try {
          return encodeURIComponent(decodeURIComponent(seg));
        } catch {
          return encodeURIComponent(seg);
        }
      })
      .join("/");
  if (/^https?:\/\//i.test(s)) {
    try {
      const u = new URL(s);
      const parts = u.pathname.split("/").filter(Boolean);
      u.pathname = `/${parts.map((seg) => encodePathSegments(seg)).join("/")}`;
      return u.toString();
    } catch {
      return encodeURI(s);
    }
  }
  const base = (process.env.REACT_APP_S3_PATH || "").replace(/\/?$/, "/");
  const path = s.replace(/^\/+/, "");
  return `${base}${encodePathSegments(path)}`;
}

/**
 * Value for CSS `background-image` / `url(...)`. Unquoted `url(https://.../a(2).png)` is invalid — the `(` ends the URL.
 */
export function toCssUrlValue(href) {
  if (!href) return "none";
  const escaped = String(href).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `url("${escaped}")`;
}

export const ITEMS_MODE_IMAGE = "image";
export const ITEMS_MODE_NAME = "name";

export function normalizeCategoryItemsMode(val) {
  const s = String(val ?? "").toLowerCase();
  return s === ITEMS_MODE_NAME ? ITEMS_MODE_NAME : ITEMS_MODE_IMAGE;
}

export function getCategoryItemsMode(themeData, categoryIndex) {
  return normalizeCategoryItemsMode(themeData?.[`category${categoryIndex}_items_mode`]);
}

/** Pick a background for a name row when there are fewer BG images than names (reuses pool). */
export function pickItemBgForNameIndex(bgs, nameIndex, categoryIndex = 1, random = false) {
  if (!Array.isArray(bgs) || !bgs.length) return null;
  if (random) {
    return bgs[Math.floor(Math.random() * bgs.length)];
  }
  const j = (nameIndex * 17 + categoryIndex * 31) % bgs.length;
  return bgs[j];
}

export function countCategoryItemNames(themeData, categoryIndex) {
  return ensureThemeJsonArray(themeData?.[`category${categoryIndex}_item_names`]).filter((n) =>
    String(n ?? "").trim()
  ).length;
}

/** Build conveyor belt items from per-category mode (images vs text-on-background). */
export function buildBeltItemsFromTheme(themeData) {
  if (!themeData || typeof themeData !== "object") return [];
  const images = [];
  let seq = 0;
  const ts = Date.now();

  for (let cat = 1; cat <= 3; cat++) {
    const mode = getCategoryItemsMode(themeData, cat);
    if (mode === ITEMS_MODE_NAME) {
      const names = ensureThemeJsonArray(themeData[`category${cat}_item_names`]);
      const bgs = ensureThemeJsonArray(themeData[`category${cat}_item_bg_images`]);
      names.forEach((rawName, i) => {
        const label = String(rawName ?? "").trim();
        if (!label) return;
        const bgPath = pickItemBgForNameIndex(bgs, i, cat, true);
        if (!bgPath) return;
        images.push({
          id: `item-c${cat}-n${i}-${seq++}-${ts}`,
          url: getDvSourceImageUrl(bgPath),
          label,
          itemKind: ITEMS_MODE_NAME,
          category: cat,
        });
      });
    } else {
      ensureThemeJsonArray(themeData[`category${cat}_items`]).forEach((imgPath) => {
        if (!imgPath) return;
        images.push({
          id: `item-c${cat}-i${seq++}-${ts}`,
          url: getDvSourceImageUrl(imgPath),
          itemKind: ITEMS_MODE_IMAGE,
          category: cat,
        });
      });
    }
  }
  return images;
}

export function countThemeBeltItems(themeData) {
  if (!themeData || typeof themeData !== "object") return 0;
  let n = 0;
  for (let cat = 1; cat <= 3; cat++) {
    const mode = getCategoryItemsMode(themeData, cat);
    if (mode === ITEMS_MODE_NAME) {
      n += countCategoryItemNames(themeData, cat);
    } else {
      n += ensureThemeJsonArray(themeData[`category${cat}_items`]).length;
    }
  }
  return n;
}
